/**
 * Routes List Page
 * Display all routes with pagination, filtering, and actions
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, Eye, Edit2, Play, Pause, X, Filter } from 'lucide-react';
import { transportApi, type RawMaterialRoute } from '../../services/transport/api';

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' },
  ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' }
};

export default function RouteListPage() {
  const navigate = useNavigate();
  const [allRoutes, setAllRoutes] = useState<RawMaterialRoute[]>([]);
  const [routes, setRoutes] = useState<RawMaterialRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const pageSize = 10;

  // Fetch all routes once on mount
  useEffect(() => {
    fetchRoutes();
  }, []);

  // Apply filters and pagination whenever filters, page, or data changes
  useEffect(() => {
    let filtered = allRoutes;

    if (statusFilter) {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) =>
        r.routeName?.toLowerCase().includes(term) ||
        r.routeCode?.toLowerCase().includes(term) ||
        r.mfgUnitName?.toLowerCase().includes(term)
      );
    }

    setTotalElements(filtered.length);
    const start = page * pageSize;
    setRoutes(filtered.slice(start, start + pageSize));
  }, [allRoutes, statusFilter, searchTerm, page]);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const response = await transportApi.listRoutes(0, 1000);
      setAllRoutes(response.content || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load routes');
      setAllRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (routeId: number, newStatus: string) => {
    try {
      let updatedRoute;
      switch (newStatus) {
        case 'ACTIVE':
          updatedRoute = await transportApi.activateRoute(routeId);
          break;
        case 'RESUMED':
          updatedRoute = await transportApi.resumeRoute(routeId);
          break;
        case 'PAUSED':
          updatedRoute = await transportApi.pauseRoute(routeId);
          break;
        case 'CANCELLED':
          updatedRoute = await transportApi.cancelRoute(routeId);
          break;
        default:
          return;
      }

      setAllRoutes((prev) =>
        prev.map((r) => (r.id === routeId ? updatedRoute : r))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update route');
    }
  };

  const totalPages = Math.ceil(totalElements / pageSize);

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Routes</h2>
        <button
          onClick={() => navigate('/transport/raw-material/routes/create')}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Create Route
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Route name, code, or manufacturing unit..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Routes Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : routes.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-gray-600">No routes found</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Route</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Manufacturing Unit</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Stops</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Driver</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Distance</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((route) => {
                const statusColor = statusColors[route.status || 'DRAFT'];
                return (
                  <tr key={route.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{route.routeName}</p>
                        <p className="text-sm text-gray-600">{route.routeCode}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{route.mfgUnitName}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{route.totalStops ?? route.stops?.length ?? 0} stops</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{route.assignedDriverName || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 rounded-full ${statusColor.bg} ${statusColor.text} px-3 py-1 text-xs font-medium`}>
                        <span className={`h-2 w-2 rounded-full ${statusColor.dot}`}></span>
                        {route.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{route.totalDistanceKm?.toFixed(1) || '-'} km</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/transport/raw-material/routes/${route.id}`)}
                          className="rounded-lg p-2 hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        {route.status === 'ACTIVE' && (
                          <button
                            onClick={() => handleStatusChange(route.id!, 'PAUSED')}
                            className="rounded-lg p-2 hover:bg-gray-100 text-yellow-600 hover:text-yellow-900"
                            title="Pause"
                          >
                            <Pause size={18} />
                          </button>
                        )}
                        {route.status === 'PAUSED' && (
                          <button
                            onClick={() => handleStatusChange(route.id!, 'RESUMED')}
                            className="rounded-lg p-2 hover:bg-gray-100 text-green-600 hover:text-green-900"
                            title="Resume"
                          >
                            <Play size={18} />
                          </button>
                        )}
                        {route.status === 'DRAFT' && (() => {
                          const missingReqs: string[] = [];
                          if (!route.assignedDriverId) missingReqs.push('driver');
                          if (!route.assignedVehicleId) missingReqs.push('vehicle');
                          const stopsCount = route.totalStops ?? route.stops?.length ?? 0;
                          if (stopsCount === 0) missingReqs.push('at least one stop');
                          const canActivate = missingReqs.length === 0;
                          const tooltipText = canActivate
                            ? 'Activate'
                            : `Cannot activate: assign ${missingReqs.join(', ')} first`;
                          return (
                            <button
                              onClick={() => canActivate && handleStatusChange(route.id!, 'ACTIVE')}
                              disabled={!canActivate}
                              className={`rounded-lg p-2 ${canActivate ? 'hover:bg-gray-100 text-green-600 hover:text-green-900' : 'text-gray-300 cursor-not-allowed'}`}
                              title={tooltipText}
                            >
                              <Play size={18} />
                            </button>
                          );
                        })()}
                        {(route.status === 'ACTIVE' || route.status === 'PAUSED' || route.status === 'DRAFT') && (
                          <button
                            onClick={() => handleStatusChange(route.id!, 'CANCELLED')}
                            className="rounded-lg p-2 hover:bg-gray-100 text-red-600 hover:text-red-900"
                            title="Cancel"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">
            Page {page + 1} of {totalPages} ({totalElements} total routes)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-medium disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
